import { useState } from 'react'
import type { BadgeWithProgress } from '../../types'
import { RARITY, rarityEmoji, getCriteriaDescription } from './rarity'

interface BadgeCardProps {
  badge: BadgeWithProgress
  onClick?: () => void
}

const T = { sora: "'Sora', sans-serif", inter: "'Inter', sans-serif" }

interface BadgeIconProps {
  badge: BadgeWithProgress
  size?: number
}

export function BadgeIcon({ badge, size = 52 }: BadgeIconProps) {
  const r = RARITY[badge.rarity]
  const earned = !!badge.earned_at
  const isLegendary = badge.rarity === 'legendary'
  const emoji = rarityEmoji(badge.rarity, badge.category)

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {earned && (
        <div
          style={{
            position: 'absolute',
            inset: -3,
            borderRadius: '50%',
            background: isLegendary
              ? 'linear-gradient(135deg,#fde68a,#d97706,#fde68a)'
              : `conic-gradient(${r.border}, transparent, ${r.border})`,
            animation: isLegendary ? 'spin 3s linear infinite' : undefined,
            zIndex: 0,
          }}
        />
      )}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size * 0.48,
          background: earned
            ? isLegendary
              ? 'linear-gradient(135deg,#fffbeb,#fef3c7)'
              : '#fffbeb'
            : '#f5f5f4',
          border: earned ? `2px solid ${r.border}` : '2px solid #e7e5e4',
          boxShadow: earned ? `0 0 12px ${r.glow}, 0 2px 8px rgba(0,0,0,.08)` : 'none',
          filter: earned ? 'none' : 'grayscale(1) opacity(0.45)',
          transition: 'all .2s',
          overflow: 'hidden',
        }}
      >
        {badge.icon_url ? (
          <img
            src={badge.icon_url}
            alt={badge.name}
            style={{ width: '70%', height: '70%', objectFit: 'contain' }}
          />
        ) : (
          emoji
        )}
      </div>
      {earned && (
        <div
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            zIndex: 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'linear-gradient(135deg,#d97706,#f59e0b)',
            border: '2px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            color: '#fff',
            fontWeight: 700,
            boxShadow: '0 1px 4px rgba(217,119,6,.5)',
          }}
        >
          ✓
        </div>
      )}
    </div>
  )
}

interface RarityPillProps {
  badge: Pick<BadgeWithProgress, 'rarity'>
}

export function RarityPill({ badge }: RarityPillProps) {
  const r = RARITY[badge.rarity]
  const isLegendary = badge.rarity === 'legendary'
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 9999,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: T.inter,
        background: isLegendary ? 'linear-gradient(135deg,#fef9c3,#fde68a)' : r.bg,
        border: `1px solid ${r.border}`,
        color: r.text,
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {isLegendary ? '✦ ' : ''}
      {r.label}
    </span>
  )
}

export function BadgeCard({ badge, onClick }: BadgeCardProps) {
  const [hovered, setHovered] = useState(false)
  const earned = !!badge.earned_at
  const r = RARITY[badge.rarity]
  const earnedDate = earned
    ? new Date(badge.earned_at!).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null
  const progress = badge.progress
  const pct = progress ? Math.round((progress.current / progress.required) * 100) : 0

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 14,
        border: earned ? `1px solid ${r.border}` : '1px solid #fef3c7',
        boxShadow: hovered
          ? '0 8px 20px rgba(217,119,6,.2)'
          : earned
            ? '0 2px 8px rgba(217,119,6,.14)'
            : '0 2px 6px rgba(0,0,0,.06)',
        transform: hovered ? 'translateY(-3px)' : 'none',
        transition: 'all .2s ease',
        cursor: 'pointer',
        padding: '14px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {earned && badge.rarity === 'legendary' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'linear-gradient(105deg, transparent 40%, rgba(253,230,138,.35) 50%, transparent 60%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2.5s linear infinite',
          }}
        />
      )}

      <BadgeIcon badge={badge} size={52} />

      <div style={{ textAlign: 'center', width: '100%' }}>
        <div
          style={{
            fontFamily: T.sora,
            fontWeight: 700,
            fontSize: 12.5,
            color: earned ? '#78350f' : '#57534e',
            lineHeight: 1.3,
            marginBottom: 5,
            letterSpacing: '-0.01em',
          }}
        >
          {badge.name}
        </div>
        <RarityPill badge={badge} />
      </div>

      {earned ? (
        <div
          style={{
            fontSize: 10,
            color: '#15803d',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            background: '#dcfce7',
            borderRadius: 9999,
            padding: '2px 8px',
            fontFamily: T.inter,
          }}
        >
          🏅 Earned {earnedDate}
        </div>
      ) : progress ? (
        <div style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 10, color: '#78716c', fontFamily: T.inter }}>
              {progress.current}/{progress.required}
            </span>
            <span style={{ fontSize: 10, color: '#a8a29e', fontFamily: T.inter }}>{pct}%</span>
          </div>
          <div
            style={{
              height: 5,
              borderRadius: 9999,
              background: '#f5f5f4',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 9999,
                width: `${pct}%`,
                background: 'linear-gradient(to right,#d97706,#f59e0b)',
                transition: 'width .4s ease',
              }}
            />
          </div>
          <div
            style={{
              fontSize: 10,
              color: '#a8a29e',
              marginTop: 4,
              fontFamily: T.inter,
              lineHeight: 1.4,
              textAlign: 'center',
            }}
          >
            {getCriteriaDescription(badge.criteria)}
          </div>
        </div>
      ) : (
        <div
          style={{
            fontSize: 10,
            color: '#a8a29e',
            fontFamily: T.inter,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {getCriteriaDescription(badge.criteria)}
        </div>
      )}
    </div>
  )
}
